import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Field,
  Flex,
  HStack,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  SectionCard,
  SelectInput,
  type StatusType,
} from '@themes/aegix';
import { DrawerComponent } from '../../ui/drawerComponent';
import { DetailRow } from '../DetailRow';
import { toaster } from '../../ui/toaster';
import {
  useUpdateStudentStatus,
  useReleaseStudent,
  useEventStudent,
  useUpdateEventStudent,
} from '../../../hooks/queries/useReunification';
import {
  STUDENT_STATUS_OPTIONS,
  type StudentStatus,
} from '../../../types/ReunificationTypes';
import { NotesPanel } from '../../reunification/NotesPanel';

interface StudentRowData {
  id: string;
  name: string;
  grade: string;
  homeroom: string;
  status: StatusType;
  status_label: string;
  cleared: string;
  raw_status: string;
}

interface StudentDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  student: StudentRowData | undefined;
}

export function StudentDetailDrawer({
  isOpen,
  onClose,
  eventId,
  student,
}: StudentDetailDrawerProps) {
  const [newStatus, setNewStatus] = useState('');
  const [guardianId, setGuardianId] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');

  // Notes live in the typed ReunificationNote table (ADR-121 P3) and
  // render via the shared NotesPanel. The interview flags
  // (requires_leo_interview / requires_counseling) stay on the
  // StudentReunification row because they aren't notes — they're
  // operator-set booleans that gate downstream workflow.
  const [editing, setEditing] = useState(false);
  const [editRequiresLeoInterview, setEditRequiresLeoInterview] = useState(false);
  const [editRequiresCounseling, setEditRequiresCounseling] = useState(false);

  const updateStatusMutation = useUpdateStudentStatus(eventId);
  const releaseMutation = useReleaseStudent(eventId);
  const updateStudentMutation = useUpdateEventStudent(eventId);

  // Fetch full detail when the drawer is open — drives the
  // interview-flag inline edit. This drawer reads no note fields;
  // NotesPanel is the source of truth for note display.
  const detailQuery = useEventStudent(eventId, student?.id, {
    enabled: isOpen && !!student?.id,
  });
  const detail = detailQuery.data;

  const canRelease = student?.raw_status === 'cleared_for_release';

  // Reset form state when student changes
  const handleOpen = () => {
    if (student) {
      setNewStatus(student.raw_status);
      setGuardianId('');
      setReleaseNotes('');
    }
  };

  // Ensure form state is set when drawer opens
  if (isOpen && student && newStatus === '') {
    handleOpen();
  }

  // Reset interview-flag edit form whenever a new detail row lands,
  // or the drawer closes.
  useEffect(() => {
    if (isOpen && detail) {
      setEditRequiresLeoInterview(!!detail.requires_leo_interview);
      setEditRequiresCounseling(!!detail.requires_counseling);
    }
    if (!isOpen) {
      setEditing(false);
    }
  }, [isOpen, detail]);

  const hasEditChanges = useMemo(() => {
    if (!detail) return false;
    return (
      editRequiresLeoInterview !== !!detail.requires_leo_interview ||
      editRequiresCounseling !== !!detail.requires_counseling
    );
  }, [detail, editRequiresLeoInterview, editRequiresCounseling]);

  const handleSaveEdit = () => {
    if (!student || !detail || !hasEditChanges) return;
    const data: Parameters<typeof updateStudentMutation.mutate>[0]['data'] = {};
    if (editRequiresLeoInterview !== !!detail.requires_leo_interview) {
      data.requires_leo_interview = editRequiresLeoInterview;
    }
    if (editRequiresCounseling !== !!detail.requires_counseling) {
      data.requires_counseling = editRequiresCounseling;
    }
    updateStudentMutation.mutate(
      { studentId: student.id, data },
      {
        onSuccess: () => {
          toaster.create({ title: 'Student updated', type: 'success' });
          setEditing(false);
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to update student',
            description: err instanceof Error ? err.message : 'Unknown error',
            type: 'error',
          });
        },
      },
    );
  };

  const handleCancelEdit = () => {
    if (detail) {
      setEditRequiresLeoInterview(!!detail.requires_leo_interview);
      setEditRequiresCounseling(!!detail.requires_counseling);
    }
    setEditing(false);
  };

  const handleUpdateStatus = () => {
    if (!student || !newStatus) return;
    updateStatusMutation.mutate(
      // `newStatus` is held as a string in form state; the SelectInput is
      // bound to STUDENT_STATUS_OPTIONS so the value is always a valid
      // StudentStatus by the time this runs. Cast to satisfy the typed
      // mutation generic.
      { studentId: student.id, data: { status: newStatus as StudentStatus } },
      {
        onSuccess: () => {
          toaster.create({ title: 'Student status updated', type: 'success' });
          onClose();
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to update status',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleRelease = () => {
    if (!student || !guardianId) return;
    releaseMutation.mutate(
      {
        studentId: student.id,
        data: { guardian_id: guardianId, notes: releaseNotes || undefined },
      },
      {
        onSuccess: () => {
          toaster.create({ title: 'Student released', type: 'success' });
          onClose();
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to release student',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleClose = () => {
    setNewStatus('');
    setGuardianId('');
    setReleaseNotes('');
    onClose();
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title="Student Detail"
      size="md"
      closeOnOutside={false}
      closeOnEsc={false}
      footer={
        <HStack gap={3} width="100%">
          <ButtonComponent
            visual="primary"
            onClick={handleUpdateStatus}
            disabled={
              updateStatusMutation.isPending ||
              newStatus === student?.raw_status
            }
            data-e2e="update-button">
            {updateStatusMutation.isPending ? 'Saving...' : 'Update Status'}
          </ButtonComponent>
          {canRelease && (
            <ButtonComponent
              visual="primary"
              onClick={handleRelease}
              disabled={releaseMutation.isPending || !guardianId}
              data-e2e="release-button">
              {releaseMutation.isPending ? 'Releasing...' : 'Release'}
            </ButtonComponent>
          )}
          <ButtonComponent visual="secondary" onClick={handleClose} data-e2e="close-button">
            Close
          </ButtonComponent>
        </HStack>
      }
    >
      {student ? (
        <Flex direction="column" gap={4}>
          <SectionCard title="Student Details">
            <VStack align="stretch" gap={0}>
              <DetailRow label="Name" value={student.name} dataE2e="student-detail-name" />
              <DetailRow label="Grade" value={student.grade} dataE2e="student-detail-grade" />
              <DetailRow label="Homeroom" value={student.homeroom} dataE2e="student-detail-homeroom" />
              <DetailRow label="Current Status" value={student.status_label} dataE2e="student-detail-status" />
              <DetailRow label="Cleared" value={student.cleared} dataE2e="student-detail-cleared" />
            </VStack>
          </SectionCard>

          {/* Typed/tagged notes via NotesPanel (ADR-121 P3). Server is
              the source of truth for visibility — restricted notes
              simply don't appear in the response when the caller
              lacks the permission. */}
          <NotesPanel
            eventId={eventId}
            subject={{ student: student.id }}
            e2ePrefix="student-notes-panel"
          />

          {/* Interview flags stay on the StudentReunification row
              because they aren't notes — they're operator-set
              booleans that drive downstream workflow (LEO
              interview / counseling routing). Inline edit pattern
              matches StaffDetailDrawer's Edit section. */}
          <SectionCard title="Edit Details">
            {detailQuery.isLoading ? (
              <HStack py={4} justify="center">
                <Spinner size="sm" color="button.primary" />
              </HStack>
            ) : detailQuery.error ? (
              <Text fontSize="sm" color="text.danger" py={2}>
                {detailQuery.error instanceof Error
                  ? detailQuery.error.message
                  : 'Failed to load detail'}
              </Text>
            ) : !editing ? (
              <VStack align="stretch" gap={3}>
                <DetailRow
                  label="Requires LEO interview"
                  value={detail?.requires_leo_interview ? 'Yes' : 'No'}
                  dataE2e="student-detail-requires-leo"
                />
                <DetailRow
                  label="Requires counseling"
                  value={detail?.requires_counseling ? 'Yes' : 'No'}
                  dataE2e="student-detail-requires-counseling"
                />
                <ButtonComponent
                  visual="secondary"
                  onClick={() => setEditing(true)}
                  disabled={!detail}
                  data-e2e="student-detail-edit-button"
                >
                  Edit details
                </ButtonComponent>
              </VStack>
            ) : (
              <VStack align="stretch" gap={3}>
                <Checkbox.Root
                  checked={editRequiresLeoInterview}
                  onCheckedChange={(details) =>
                    setEditRequiresLeoInterview(details.checked === true)
                  }
                  disabled={updateStudentMutation.isPending}
                  data-e2e="student-edit-requires-leo"
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label fontSize="sm">
                    Requires LEO interview
                  </Checkbox.Label>
                </Checkbox.Root>
                <Checkbox.Root
                  checked={editRequiresCounseling}
                  onCheckedChange={(details) =>
                    setEditRequiresCounseling(details.checked === true)
                  }
                  disabled={updateStudentMutation.isPending}
                  data-e2e="student-edit-requires-counseling"
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label fontSize="sm">
                    Requires counseling
                  </Checkbox.Label>
                </Checkbox.Root>
                <HStack gap={2} justify="flex-end" pt={2}>
                  <ButtonComponent
                    visual="secondary"
                    onClick={handleCancelEdit}
                    disabled={updateStudentMutation.isPending}
                    data-e2e="student-edit-cancel"
                  >
                    Cancel
                  </ButtonComponent>
                  <ButtonComponent
                    visual="primary"
                    onClick={handleSaveEdit}
                    disabled={
                      updateStudentMutation.isPending || !hasEditChanges
                    }
                    data-e2e="student-edit-save"
                  >
                    {updateStudentMutation.isPending ? 'Saving…' : 'Save'}
                  </ButtonComponent>
                </HStack>
              </VStack>
            )}
          </SectionCard>

          <SectionCard title="Update Status">
            <SelectInput
              label="New Status"
              options={STUDENT_STATUS_OPTIONS}
              value={newStatus}
              onChange={(value) => setNewStatus(value)}
              e2eTitle="student-status-select"
            />
          </SectionCard>

          {canRelease && (
            <SectionCard title="Release to Guardian">
              <VStack align="stretch" gap={3}>
                <Field.Root gap={0}>
                  <Field.Label fontSize="sm" color="text.secondary" mb={1}>
                    Guardian ID
                  </Field.Label>
                  <Input
                    size="sm"
                    placeholder="Enter guardian public ID"
                    value={guardianId}
                    onChange={(e) => setGuardianId(e.target.value)}
                    data-e2e="release-guardian-id-input"
                  />
                </Field.Root>
                <Field.Root gap={0}>
                  <Field.Label fontSize="sm" color="text.secondary" mb={1}>
                    Notes (optional)
                  </Field.Label>
                  <Textarea
                    size="sm"
                    placeholder="Release notes..."
                    value={releaseNotes}
                    onChange={(e) => setReleaseNotes(e.target.value)}
                    data-e2e="release-notes-input" />
                </Field.Root>
              </VStack>
            </SectionCard>
          )}
        </Flex>
      ) : null}
    </DrawerComponent>
  );
}
